import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { achievementService } from '@/services';
import { profileService } from '@/services/ProfileService';
import { AchievementNotification } from '@/components/achievement/AchievementNotification';

// Global flag to prevent multiple initialization checks
// This prevents React's StrictMode or other lifecycle events from triggering multiple checks
let hasPerformedInitialCheck = false;

interface AchievementCheckerProps {
  children: React.ReactNode;
  triggerCheck?: boolean; // Used to trigger achievement check after specific events
}

/**
 * Component that checks for new achievements at app startup and when triggered
 * This component should be placed high in the component tree to ensure it has access to auth context
 */
const AchievementChecker: React.FC<AchievementCheckerProps> = ({ 
  children,
  triggerCheck = false 
}) => {
  const { user, isAuthenticated } = useAuth();
  const [earnedAchievements, setEarnedAchievements] = useState<string[]>([]);
  const [showNotification, setShowNotification] = useState(false);
  const [currentAchievementIndex, setCurrentAchievementIndex] = useState(0);
  
  // Reference to store the last time achievements were checked
  const lastCheckTimeRef = useRef<number>(0);
  // Throttle interval in milliseconds (2 seconds)
  const THROTTLE_INTERVAL = 2000;
  // Track if the component has already performed its initial check in this session
  const hasCheckedInThisSession = useRef<boolean>(false);
  
  // Check for achievements at startup - ONLY ONCE
  useEffect(() => {
    if (isAuthenticated && user?.id && !hasPerformedInitialCheck) {
      console.log('🏆 [AchievementChecker] Initial achievement check', { userId: user.id });
      hasPerformedInitialCheck = true;
      hasCheckedInThisSession.current = true;
      
      // Run the complete achievement check cycle
      checkAchievementsCycle();
    }
  }, [isAuthenticated, user?.id]);
  
  // Clean up the check flag when component unmounts
  useEffect(() => {
    return () => {
      hasCheckedInThisSession.current = false;
    };
  }, []);
  
  // Check when triggerCheck changes to true
  useEffect(() => {
    if (triggerCheck && isAuthenticated && user?.id) {
      console.log('🏆 [AchievementChecker] Trigger-based achievement check', { userId: user.id });
      checkAchievementsCycle();
    }
  }, [triggerCheck, isAuthenticated, user?.id]);
  
  // Display notifications one after another
  useEffect(() => {
    if (earnedAchievements.length > 0 && currentAchievementIndex < earnedAchievements.length) {
      setShowNotification(true);
    } else {
      setShowNotification(false);
    }
  }, [earnedAchievements, currentAchievementIndex]);
  
  // Throttled achievement check to prevent too frequent checks
  const throttledCheckAchievements = () => {
    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckTimeRef.current;
    
    if (timeSinceLastCheck < THROTTLE_INTERVAL) {
      console.log(`🏆 [AchievementChecker] Throttling achievement check, last check was ${timeSinceLastCheck}ms ago`);
      return;
    }
    
    // For startup checks, if another instance has already checked, skip
    if (!triggerCheck && hasCheckedInThisSession.current && lastCheckTimeRef.current > 0) {
      console.log('🏆 [AchievementChecker] Skipping duplicate initial check');
      return;
    }
    
    // Update the last check time
    lastCheckTimeRef.current = now;
    checkAchievements();
  };
  
  // Full achievement check cycle - checks unviewed first, then new
  const checkAchievementsCycle = async () => {
    if (!user?.id) {
      console.log('🏆 [AchievementChecker] User not authenticated, skipping achievement check');
      return;
    }

    try {
      // First check for unviewed achievements
      console.log('🏆 [AchievementChecker] Starting full achievement check cycle...');
      const unviewedAchievementIds = await profileService.getUnviewedAchievements(user.id);
      
      if (unviewedAchievementIds.length > 0) {
        // If we found unviewed achievements, show them and don't check for new ones yet
        console.log(`🏆 [AchievementChecker] Found ${unviewedAchievementIds.length} unviewed achievements`, unviewedAchievementIds);
        setShowNotification(true); // Make sure notification is visible
        setEarnedAchievements(unviewedAchievementIds);
        setCurrentAchievementIndex(0);
        return; // Exit early - we'll check for more after these are dismissed
      }
      
      // Only check for new achievements if no unviewed ones were found
      console.log('🏆 [AchievementChecker] No unviewed achievements, checking for new ones...');
      const hasNewAchievements = await checkAchievements(); // Call directly instead of throttled version
      
      // After checking, update UI display state if needed
      if (hasNewAchievements) {
        setShowNotification(true);
      }
    } catch (error) {
      console.error('❌ [AchievementChecker] Error in achievement check cycle:', error);
    }
  };

  // Check for unviewed achievements in the user's profile
  const checkForUnviewedAchievements = async () => {
    if (!user?.id) return false;

    try {
      console.log('🏆 [AchievementChecker] Checking for unviewed achievements...');
      const unviewedAchievementIds = await profileService.getUnviewedAchievements(user.id);
      
      if (unviewedAchievementIds.length > 0) {
        console.log(`🏆 [AchievementChecker] Found ${unviewedAchievementIds.length} unviewed achievements`, unviewedAchievementIds);
        setEarnedAchievements(unviewedAchievementIds);
        setCurrentAchievementIndex(0);
        return true; // Found unviewed achievements
      } else {
        console.log('🏆 [AchievementChecker] No unviewed achievements found');
        return false; // No unviewed achievements
      }
    } catch (error) {
      console.error('❌ [AchievementChecker] Error checking unviewed achievements:', error);
      return false;
    }
  };

  const checkAchievements = async () => {
    if (!user?.id) return;
    
    try {
      console.log('🏆 [AchievementChecker] Starting new achievement check...');
      const newAchievements = await achievementService.checkAllAchievements(user.id);
      
      if (newAchievements.length > 0) {
        console.log(`🏆 [AchievementChecker] User earned ${newAchievements.length} new achievements!`, newAchievements);
        setEarnedAchievements(newAchievements);
        setCurrentAchievementIndex(0);
        return true; // Found new achievements
      } else {
        console.log('🏆 [AchievementChecker] No new achievements earned');
        return false; // No new achievements
      }
    } catch (error) {
      console.error('❌ [AchievementChecker] Error checking achievements:', error);
      return false;
    }
  };
  
  const handleDismissNotification = async () => {
    console.log('🏆 [AchievementChecker] handleDismissNotification called');
    
    // Mark the current achievement as viewed in Firestore
    if (user?.id && currentAchievementId) {
      try {
        await profileService.markAchievementAsViewed(user.id, currentAchievementId);
        console.log('🏆 [AchievementChecker] Achievement marked as viewed:', currentAchievementId);
      } catch (error) {
        console.error('❌ [AchievementChecker] Error marking achievement as viewed:', error);
      }
    }

    // Move to next achievement in the queue
    if (currentAchievementIndex < earnedAchievements.length - 1) {
      console.log('🏆 [AchievementChecker] Moving to next achievement');
      
      // Briefly hide notification to ensure clean transition between achievements
      setShowNotification(false);
      
      // Then show the next achievement after a short delay
      setTimeout(() => {
        // Increment to show the next achievement
        setCurrentAchievementIndex(prev => {
          console.log(`🏆 [AchievementChecker] Advancing achievement index from ${prev} to ${prev + 1}`);
          return prev + 1;
        });
        
        // Make notification visible again with the new achievement
        setTimeout(() => {
          console.log('🏆 [AchievementChecker] Showing next achievement notification');
          setShowNotification(true);
        }, 50);
      }, 300);
    } else {
      console.log('🏆 [AchievementChecker] All achievements shown, resetting state');
      // Reset when all achievements have been shown
      setShowNotification(false);
      setEarnedAchievements([]);
      setCurrentAchievementIndex(0);
      
      // After showing all achievements, run the full check cycle again
      // This ensures we catch both unviewed and new achievements
      if (user?.id) {
        console.log('🏆 [AchievementChecker] Running achievement check after final dismissal');
        // Run immediately without timeout
        checkAchievementsCycle();
      }
    }
  };
  
  // Get current achievement to display
  const currentAchievementId = earnedAchievements[currentAchievementIndex];
  const currentAchievement = currentAchievementId 
    ? achievementService.getAchievementById(currentAchievementId)
    : null;
    
  return (
    <>
      {children}
      
      {/* Achievement notification overlay */}
      {showNotification && currentAchievement && (
        <AchievementNotification 
          achievement={currentAchievement}
          onDismiss={handleDismissNotification}
          isLastAchievement={currentAchievementIndex === earnedAchievements.length - 1}
        />
      )}
      
      {/* Debug info: This is invisible on the UI but helps with debugging */}
      <div style={{ display: 'none' }}>
        achievements: {earnedAchievements.length} | 
        current: {currentAchievementIndex} | 
        showing: {showNotification ? 'yes' : 'no'}
      </div>
    </>
  );
};

export default AchievementChecker;
